import { ExpandAltOutlined } from '@ant-design/icons';
import { useQuery } from '@apollo/client';
import { Radio, Table, type TableProps, Tag, Tooltip } from 'antd';
import type { SorterResult } from 'antd/es/table/interface';
import { isBoolean, isEmpty, isString } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import type { ReligiousTransparencies_ConnectionQuery } from '../../../../__generated__/graphql';
import { DownVote, UpVote } from '../../../../assets/svg';
import { FINAL_RATING_OPTIONS, LIMIT } from '../../../../common/constants';
import {
  formatCurrency,
  notEmpty,
  titleCase,
  truncateToChars,
} from '../../../../common/utils';
import ActionBtn from '../../../../components/ActionBtn';
import useRouter from '../../../../hooks/useRouter';
import { VoteEnum } from '../../../../types/common.type';
import { GET_RELIGIOUS_TRANSPARENCY_ANALYSES } from '../../graphql/queries';
import ReligiousTransparencyDetailsModal, {
  type FormValues,
} from './ReligiousTransparencyDetailsModal';

export type ReligiousTransparencyAnalysesData = NonNullable<
  NonNullable<
    ReligiousTransparencies_ConnectionQuery['religiousTransparencies_connection']
  >['nodes']
>[number];

const ReligiousTransparencyAnalysis = ({
  externalFiltersSection,
  onTotalChange,
  activeTab,
  setIsDataPresent,
}: {
  externalFiltersSection?: {
    section?: {
      label: string;
      value: string;
      key: string;
    } | null;
    isProcessed?: boolean;
  };
  onTotalChange: ({ token, count }: { token: number; count: number }) => void;
  activeTab:
    | 'factCheck'
    | 'category'
    | 'sexually-inappropriate'
    | 'religious-transparency';
  setIsDataPresent: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const {
    params: { bookId = '' },
  } = useRouter();

  const [
    religiousTransparencyAnalysesData,
    setReligiousTransparencyAnalysesData,
  ] = useState<ReligiousTransparencyAnalysesData[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [totalData, setTotalData] = useState<number>(0);

  const modifyReligiousTransparencyData = (id: string, value: FormValues) => {
    const modifyData = religiousTransparencyAnalysesData.map((item) =>
      item.documentId === id
        ? {
            ...item,
            subSection: {
              ...item.subSection,
              documentId: item.subSection?.documentId ?? '',
              feedback: {
                ...item.subSection?.feedback,
                documentId: item.subSection?.feedback?.documentId ?? '',
                ...value,
              },
            },
          }
        : item,
    );

    setReligiousTransparencyAnalysesData(modifyData);
  };

  const [variables, setVariables] = useState({
    filters: {
      book: {
        documentId: {
          eq: bookId ?? '',
        },
      },
      subSection: {},
    },
    pagination: {
      page: 1,
      pageSize: LIMIT,
    },
    sort: ['createdAt:asc'],
  });

  const {
    data: religiousTransparencyDataList,
    loading,
    refetch,
  } = useQuery(GET_RELIGIOUS_TRANSPARENCY_ANALYSES, {
    skip:
      isEmpty(bookId) || !activeTab || activeTab !== 'religious-transparency',
    variables: {
      ...variables,
      filters: {
        ...variables.filters,
        subSection: {
          ...variables?.filters?.subSection,
          ...(isBoolean(externalFiltersSection?.isProcessed)
            ? {
                isProcessed: {
                  eq: externalFiltersSection?.isProcessed,
                },
              }
            : { isProcessed: undefined }),
        },
        ...(externalFiltersSection?.section
          ? {
              section: {
                title: {
                  eqi: externalFiltersSection.section.value,
                },
              },
            }
          : { section: undefined }),
      },
    },
    onCompleted({ religiousTransparencies_connection }) {
      setTotalData(religiousTransparencies_connection?.pageInfo.total ?? 0);
      const sectionIsSelected = externalFiltersSection?.section;
      const religiousTransparencyData =
        religiousTransparencies_connection?.nodes?.[0];
      onTotalChange({
        token: sectionIsSelected
          ? religiousTransparencyData?.section?.tokenCounts ?? 0
          : religiousTransparencyData?.book?.tokenCounts ?? 0,
        count: sectionIsSelected
          ? religiousTransparencyData?.section?.priceCounts ?? 0
          : religiousTransparencyData?.book?.priceCounts ?? 0,
      });

      const religiousTransparency =
        religiousTransparencies_connection?.nodes?.filter(notEmpty);
      const newData = religiousTransparency?.map((religiousTransparency) => ({
        ...religiousTransparency,
        key: religiousTransparency?.documentId,
      }));
      setReligiousTransparencyAnalysesData(newData ?? []);
      setIsDataPresent(!!newData?.length);
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    onError() {
      onTotalChange({
        token: 0,
        count: 0,
      });
    },
  });

  const getSortOrder = (column: 'overallBiasScore') => {
    const sorting = variables?.sort?.[0];
    const [sortOn, sortBy] = sorting?.split(':') ?? [];
    return sortOn === column ? (sortBy === 'asc' ? 'ascend' : 'descend') : null;
  };

  const columns: TableProps<ReligiousTransparencyAnalysesData>['columns'] = [
    {
      title: 'Input (Paragraph)',
      dataIndex: ['subSection', 'paragraph'],
      key: 'paragraph',
      width: 100,
      render: (value) => truncateToChars(value, 100),
    },
    {
      title: 'Overall Bias Score',
      dataIndex: 'overallBiasScore',
      key: 'overallBiasScore',
      width: 40,
      sorter: true,
      defaultSortOrder: 'ascend',
      sortOrder: getSortOrder('overallBiasScore'),
      render: (overallBiasScore) => {
        return <p>{overallBiasScore ?? '-'}</p>;
      },
      filterMultiple: false,
      filters: FINAL_RATING_OPTIONS.map(({ label, value }) => ({
        text: label,
        value,
      })),
    },
    {
      title: 'Stance By Religion',
      dataIndex: 'draftStanceByReligion',
      key: 'draftStanceByReligion',
      width: 35,
      render: (draftStanceByReligion) => {
        if (isEmpty(draftStanceByReligion)) return <p>-</p>;

        return (
          <div className="d-flex gap-8 flex-wrap min-w-[70px]">
            {Object.entries(draftStanceByReligion)
              .filter(([_, stance]) => isString(stance))
              .map(([religion, stance]) => (
                <Tag key={religion} className="tag-ellipsis">
                  {titleCase(religion)}: {stance as string}
                </Tag>
              ))}
          </div>
        );
      },
    },
    {
      title: 'Keyword List',
      dataIndex: 'keywordList',
      key: 'keywordList',
      width: 70,
      render: (keywordList) => {
        if (isEmpty(keywordList)) return <p>-</p>;
        return (
          <div className="d-flex gap-8 flex-wrap min-w-[70px]">
            {keywordList.map((keyword: string) => (
              <Tag key={keyword} className="tag-ellipsis">
                {titleCase(keyword)}
              </Tag>
            ))}
          </div>
        );
      },
    },
    {
      title: 'Rating Reason',
      dataIndex: 'reasonNote',
      key: 'reasonNote',
      width: 100,
      render: (value) => truncateToChars(value, 100),
    },
    {
      title: 'Feedback',
      dataIndex: ['subSection', 'feedback'],
      key: 'feedback',
      width: 20,
      render: (value) => {
        return value ? (
          value.religiousUpvote ? (
            <Tooltip title={value?.religiousClientComments ?? ''}>
              <Radio.Group className="vote">
                <Radio.Button value={true} className="up-vote">
                  <UpVote />
                </Radio.Button>
              </Radio.Group>
            </Tooltip>
          ) : value.religiousDownvote ? (
            <Tooltip title={value?.religiousClientComments ?? ''}>
              <Radio.Group className="vote">
                <Radio.Button value={false} className="down-vote">
                  <DownVote />
                </Radio.Button>
              </Radio.Group>
            </Tooltip>
          ) : (
            '-'
          )
        ) : (
          '-'
        );
      },
      filterMultiple: false,
      filters: [
        {
          text: VoteEnum.UpVote,
          value: VoteEnum.UpVote,
        },
        {
          text: VoteEnum.DownVote,
          value: VoteEnum.DownVote,
        },
        {
          text: VoteEnum.NoVote,
          value: VoteEnum.NoVote,
        },
      ],
    },
    {
      title: 'Total Price',
      dataIndex: 'totalPrice',
      key: 'totalPrice',
      width: 30,
      render: (value) => formatCurrency({ value: value ?? 0 }),
    },
    {
      title: 'Action',
      dataIndex: 'documentId',
      key: 'documentId',
      fixed: 'right',
      width: 30,
      align: 'center',
      render: (_, __, index) => {
        return (
          <ActionBtn
            icon={<ExpandAltOutlined />}
            type="text"
            onClick={() => {
              setActiveIndex(index);
              setIsModalOpen(true);
            }}
          />
        );
      },
    },
  ];

  useEffect(() => {
    if (loading || activeTab === 'sexually-inappropriate') return;
    refetch();
  }, [activeTab]);

  const changePage = (page: number) => {
    setVariables((prev) => ({
      ...prev,
      pagination: {
        ...prev?.pagination,
        page,
      },
    }));
  };

  const selectReligiousTransparencyData:
    | ReligiousTransparencyAnalysesData
    | undefined = useMemo(
    () => religiousTransparencyAnalysesData[activeIndex],
    [activeIndex, religiousTransparencyAnalysesData],
  );

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const handleTableChange: TableProps<ReligiousTransparencyAnalysesData>['onChange'] =
    (pagination, filters, sorter) => {
      const { current = 1, pageSize = LIMIT } = pagination ?? {};
      const feedback = filters?.feedback?.[0]
        ? filters.feedback?.[0] === VoteEnum.UpVote
          ? { religiousUpvote: { eq: true } }
          : filters.feedback?.[0] === VoteEnum.DownVote
            ? {
                religiousUpvote: { eq: false },
                religiousDownvote: { eq: true },
              }
            : filters.feedback?.[0] === VoteEnum.NoVote
              ? { religiousUpvote: { eq: null } }
              : undefined
        : undefined;

      const overAllBiasScoreFilter = filters?.overallBiasScore?.[0]
        ? { eq: Number(filters.overallBiasScore[0]) }
        : undefined;
      const { field, order } =
        sorter as SorterResult<ReligiousTransparencyAnalysesData>;
      const sort = order
        ? [`${field}:${order === 'ascend' ? 'asc' : 'desc'}`]
        : [];
      setVariables((prev) => ({
        ...prev,
        filters: {
          ...prev?.filters,
          overallBiasScore: overAllBiasScoreFilter,
          subSection: { ...prev.filters?.subSection, feedback },
        },
        pagination: {
          ...prev.pagination,
          page: current,
          pageSize,
        },
        sort,
      }));
    };

  return (
    <div>
      <Table<ReligiousTransparencyAnalysesData>
        columns={columns}
        dataSource={religiousTransparencyAnalysesData}
        scroll={{
          x: 'min-content',
          y: 'calc(100vh - 255px)',
        }}
        pagination={{
          current: variables?.pagination?.page ?? 0,
          pageSize: variables?.pagination?.pageSize ?? LIMIT,
          total:
            religiousTransparencyDataList?.religiousTransparencies_connection
              ?.pageInfo?.total ?? 0,
        }}
        loading={loading}
        onChange={handleTableChange}
      />
      <ReligiousTransparencyDetailsModal
        modifyReligiousTransparencyData={modifyReligiousTransparencyData}
        modalOpen={isModalOpen}
        closeModal={closeModal}
        selectReligiousTransparencyData={selectReligiousTransparencyData}
        setActiveIndex={setActiveIndex}
        activeIndex={activeIndex}
        pagination={{
          ...variables?.pagination,
          total: totalData,
          listDataCount: religiousTransparencyAnalysesData?.length ?? 0,
        }}
        changePage={changePage}
        loading={loading}
        // modifySexuallyInappropriateData={modifySexuallyInappropriateData}
      />
    </div>
  );
};

export default ReligiousTransparencyAnalysis;
